import { CellContext, ColumnDef } from "@tanstack/react-table";
import classNames from "classnames";
import { DragDropChangeValue } from "components";
import { t } from "i18next";
import { useMemo } from "react";
import {
  ItemsTableDragDrop,
  PersonInfo,
  RowDragHandleCell
} from "ui";

import { DeliveryPriorityItem } from "../../deliveryPriority.types";

import styles from "./DeliveryPriorityTable.module.scss";

interface DeliveryPriorityTableProps {
  items: DeliveryPriorityItem[];
  isLoading: boolean;
  onReorder: (value: DragDropChangeValue) => void;
}

const DeliveryPriorityTable = ({
  items,
  isLoading,
  onReorder
}: DeliveryPriorityTableProps) => {
  const columns: Array<ColumnDef<DeliveryPriorityItem, DeliveryPriorityItem[]>> =
    useMemo(
      () => [
        {
          id: "order",
          header: "",
          size: 4,
          minSize: 4,
          cell: ({ row }: CellContext<DeliveryPriorityItem, DeliveryPriorityItem[]>) => (
            <RowDragHandleCell rowId={row.id} />
          )
        },
        {
          id: "position",
          accessorKey: "position",
          header: "#",
          size: 6,
          minSize: 6,
          cell: ({ row }) => row.index + 1
        },
        {
          id: "name",
          accessorKey: "name",
          header: t("Stakeholder"),
          size: 40,
          minSize: 40,
          cell: ({ row }) => (
            <PersonInfo
              image={row.original.avatar ?? undefined}
              personName={row.original.name}
              dimensions={{ width: 40, height: 40, borderRadius: "100px" }}
            />
          )
        },
        {
          id: "type",
          header: t("Type"),
          size: 15,
          minSize: 15,
          cell: ({ row }) => (
            <span
              className={classNames(
                styles.typePill,
                row.original.stakeholderType === "company"
                  ? styles["typePill--company"]
                  : styles["typePill--driver"]
              )}
            >
              {row.original.stakeholderType}
            </span>
          )
        },
        {
          id: "capacity",
          header: t("Capacity"),
          size: 15,
          minSize: 15,
          cell: ({ row }) => row.original.capacity
        },
        {
          id: "status",
          header: t("Status"),
          size: 15,
          minSize: 15,
          cell: ({ row }) =>
            row.original.isActive ? (
              t("Active")
            ) : (
              <span className={styles.inactiveLabel}>{t("Inactive")}</span>
            )
        }
      ],
      []
    );

  return (
    <ItemsTableDragDrop
      className={styles.deliveryPriorityTable}
      columns={columns}
      data={items}
      isLoading={isLoading}
      skeletonHeight={60}
      droppableId="deliveryPriorityList"
      keyForReorder="position"
      onChange={onReorder}
    />
  );
};

export default DeliveryPriorityTable;
